import get from 'lodash/get'
import filter from 'lodash/filter'
import { hasProp, arraySetter } from '@/utils'
import { wpSetting } from '@/components/utils'
import { wooSettings } from '@/campaigns/data'

import { createNamespacedHelpers } from 'vuex'
const { mapMutations, mapActions } = createNamespacedHelpers('campaigns')

export const campaign = {
	computed : {
		id () {
			return this.$get('campaign.id')
		},
		splits () {
			return this.$get('campaign.splits', [])
		},
		impressions () {
			return this.$numbers.withCommas(this.$get('campaign.analytics_data.impressions', 0))
		},
		conversions () {
			return this.$numbers.withCommas(this.$get('campaign.analytics_data.conversions', 0))
		},
		rate () {
			return this.$numbers.withCommas(this.$get('campaign.analytics_data.rate', 0))
		},
		hasSplits () {
			return 0 < this.splits.length
		},
		isSplit () {
			return this.$get('campaign.is_split', false)
		},
		campaignStatus () {
			return this.$get('campaign.status', 'paused')
		},
		campaignType () {
			return this.$get('campaign.type', 'Popup')
		},
		isPaused () {
			return 'paused' === this.campaignStatus
		},
		isPublished () {
			return 'active' === this.campaignStatus
		},
		isLegacy () {
			return this.$get('campaign.is_legacy', false)
		},
		onFire () {
			return this.$store.getters['campaigns/isOnFire'](this.id)
		},
		rulesets () {
			return this.$get('campaign.rulesets', [])
		},
		scheduledRules () {
			return this.getRules(rule => rule.type.includes('date'))
		},
		isScheduled () {
			return 0 < this.scheduledRules.length
		},
		/**
		 * Get the path to edit this campaign.
		 * @returns {string} The URI
		 */
		builderLink () {
			const returnUrl = this.isSplit ? window.location.href : null
			return this.$urls.builderLink(this.id, 'edit/', this.isLegacy, returnUrl)
		},
		/**
		 * Get the path to the campaign publish screen.
		 * @returns {string} The URI
		 */
		publishLink () {
			const returnUrl = this.isSplit ? window.location.href : null
			return this.$urls.builderLink(this.id, 'edit/#/publish', this.isLegacy, returnUrl)
		},
		/**
		 * Get the path to the campaign analytics screen.
		 * @returns {string} The URI
		 */
		analyticsLink () {
			return this.$urls.campaign(this.id, 'analytics', this.isLegacy, window.location.href)
		},
		/**
		 * Whether any of the advanced settings have been set.
		 *
		 * @since  2.0.0
		 *
		 * @returns {boolean|string} If campaign has advanced settings set.
		 */
		hasAdvancedSettings () {
			if (this.settingNever && this.settingNever.length) {
				return 'settingNever'
			}

			if (this.settingOnly && this.settingOnly.length) {
				return 'settingOnly'
			}

			if (this.settingShow && this.settingShow.length) {
				return 'settingShow'
			}

			try {
				Object.keys(this.$get('$store.state.wp.taxonomies', {})).forEach(t => {
					const value = this.$get(
						`campaign.wp.post_meta._omapi_${t}`,
						this.$get(`campaign.wp.post_meta._omapi_taxonomies.${t}`, [])
					)
					if (value && value.length) {
						throw new Error(t + ' ' + value.join(', '))
					}
				})
			} catch (e) {
				return e
			}

			try {
				wooSettings.forEach(setting => {
					const key = `campaign.wp.post_meta._omapi_${setting.id}`
					if ('1' === this.$get(key)) {
						throw new Error(setting.id)
					}
				})
			} catch (e) {
				return e
			}

			return false
		},
		/**
		 * Whether this campaign's embed is enabled.
		 * @since  2.0.0
		 */
		settingEnabled : wpSetting.call(this, {
			setting   : 'enabled',
			getter    : (val) => '1' === val,
			valueType : 'numericBool',
		}),
		/**
		 * The campaign's user-level setting.
		 * @since  2.0.0
		 */
		settingUsers : wpSetting.call(this, {
			setting      : 'users',
			defaultValue : '',
			getter       : (val) => 'all' === val ? '' : val,
		}),
		/**
		 * The campaign's inline automattic setting.
		 * @since  2.0.0
		 */
		settingAutomatic : wpSetting.call(this, {
			setting : 'automatic',
		}),
		/**
		 * The campaign's inline automattic location setting.
		 * @since  2.0.0
		 */
		settingAutoLocation : wpSetting.call(this, {
			setting : 'auto_location',
		}),
		/**
		 * The campaign's inline automattic words location value setting.
		 * @since  2.0.0
		 */
		settingAutoLocationWords : wpSetting.call(this, {
			setting      : 'auto_location_words',
			defaultValue : 300,
			valueType    : 'positiveNumber',
		}),
		/**
		 * The campaign's inline automattic paragraphs location value setting.
		 * @since  2.0.0
		 */
		settingAutoLocationParagraphs : wpSetting.call(this, {
			setting      : 'auto_location_paragraphs',
			defaultValue : 3,
			valueType    : 'positiveNumber',
		}),
		/**
		 * The campaign's "never show on" posts setting.
		 * @since  2.0.0
		 */
		settingNever : wpSetting.call(this, {
			setting      : 'never',
			defaultValue : [],
			valueType    : 'array',
		}),
		/**
		 * The campaign's "only show on" posts setting.
		 * @since  2.0.0
		 */
		settingOnly : wpSetting.call(this, {
			setting      : 'only',
			defaultValue : [],
			valueType    : 'array',
		}),
		/**
		 * The campaign's "show on" post-types/archives setting.
		 * @since  2.0.0
		 */
		settingShow : wpSetting.call(this, {
			setting      : 'show',
			defaultValue : [],
			valueType    : 'array',
			setter       : (v, k, get) => arraySetter(v, get()),
		}),
		/**
		 * The campaign's mailpoet enabled setting.
		 * @since  2.0.0
		 */
		settingMailpoet : wpSetting.call(this, {
			setting   : 'mailpoet',
			getter    : (val) => '1' === val,
			valueType : 'numericBool',
		}),
		/**
		 * The campaign's mailpoet list setting.
		 * @since  2.0.0
		 */
		settingMpList : wpSetting.call(this, {
			setting : 'mailpoet_list',
		}),
		/**
		 * The campaign's mailpoet phone field setting.
		 * @since  2.0.0
		 */
		settingMpPhone : wpSetting.call(this, {
			setting : 'mailpoet_phone_field',
		}),
	},
	methods : {
		...mapMutations([ 'updateCampaignValue', 'updateCampaignMeta' ]),
		...mapActions([ 'setStatus', 'fetchWpDataForCampaign', 'saveWordPress' ]),
		/**
		 * Show modal
		 * @param {string} type The type of modal to be shown.
		 * @returns {void}
		 */
		showModal (type) {
			this.$modal.show(this.id + `-${type}-modal`)
		},
		/**
		 * When clicking to schedule, intercept with modal if user has no access
		 * to the date rules.
		 *
		 * @since  2.0.0
		 *
		 * @param  {Object} event Event Object
		 *
		 * @returns {void}
		 */
		maybeEditSchedule (event) {
			if (!this.$store.getters.hasRuleType('date-time')) {
				event.preventDefault()

				// TODO: Use a specific upsell modal for current user-level.
				this.$bus.$emit('toggleProModal', 'CampaignScheduling', [ 'campaign-scheduling' ])
			}
		},
		/**
		 * Get a filtered subset of rules.
		 *
		 * @since  2.0.0
		 *
		 * @param  {callable} filterCb The filter callback, will run against each rule.
		 *
		 * @returns {Array} Array of rules.
		 */
		getRules (filterCb) {
			let rules = []

			if (!this.isLegacy && this.$get('rulesets', []).length) {
				this.rulesets.forEach(ruleset => {
					for (let groupId in ruleset.groups) {
						if (hasProp(ruleset.groups, groupId)) {
							const group = ruleset.groups[groupId]
							group.rules.forEach(r => {
								if (filterCb(r, group, ruleset)) {
									rules.push(r)
								}
							})
						}
					}
				})
			}

			return rules
		},
		/**
		 * Get filtered taxonomies
		 *
		 * @since  2.0.0
		 *
		 * @param  {Function} callback Callback to run on each taxonomy object.
		 *
		 * @returns {Array}            Array of filtered taxonomies.
		 */
		filterTaxonomies (callback) {
			return filter(this.taxonomies, (tax) => {
				return callback(tax)
			})
		},
		/**
		 * Get the settings from the campaign meta object.
		 *
		 * @since  2.0.0
		 *
		 * @param  {Object} meta The meta object for this campaign.
		 *
		 * @returns {Object}      The settings to save.
		 */
		getSettings (meta = null) {
			if (null === meta) {
				meta = this.$get('campaign.wp.post_meta', {})
			}

			const settings = {}

			// The _omapi_ meta which is NOT edited here should be excluded.
			const exclude = [
				'ids',
				'type',
			]

			for (let [ key, value ] of Object.entries(meta)) {
				key = key.replace('_omapi_', '')
				if (!exclude.includes(key)) {
					settings[key] = value
				}
			}

			return settings
		},
		/**
		 * Check if given taxonomy is a WooCommerce taxonomy.
		 *
		 * @since  2.0.0
		 *
		 * @param  {Object} taxonomy.name The taxonomy object name.
		 *
		 * @returns {boolean}              Whether given taxonomy is Woo.
		 */
		isWooTaxonomy ({ name }) {
			return this.$store.getters['wp/isWooActive'] && 0 === name.indexOf('product_')
		},
		/**
		 * Set the value of one of our properties using a an Event target element.
		 *
		 * @since 2.0.0
		 *
		 * @param {Object} event.target Event object
		 *
		 * @returns {void}
		 */
		setValue ({ target }) {
			this[target.name] = target.value
		},
		/**
		 * Set a compound value, based on given $ref.
		 *
		 * @since 2.0.0
		 *
		 * @param {Object} event    Event object.
		 * @param {Object} ref      The compound input reference.
		 * @param {mixed}  fallback The fallback value for the compound ref input.
		 *
		 * @returns {void}
		 */
		setCompooundValue ({ target }, ref, fallback) {
			this[target.name] = target.value
			if (get(ref, 'name')) {
				this[ref.name] = ref.value || fallback
			}
		},
		/**
		 * Set the value of one of our properties based on multiselect input event.
		 *
		 * @since 2.0.0
		 *
		 * @param {Array}  selected Array of values.
		 * @param {Object} option   Option object.
		 *
		 * @returns {void}
		 */
		setMultiSelectValue (selected, option) {
			if (get(option, 'name')) {
				this[option.name] = selected
			}
		},
		/**
		 * Updates the Campaign Status (paused/active).
		 *
		 * @since  2.0.0
		 *
		 * @param  {string} status The status, either paused or active.
		 *
		 * @returns {void}
		 */
		updateStatus (status) {
			this.updateCampaignValue({
				campaignId : this.id,
				key        : 'status',
				value      : 'paused' === status ? status : 'active',
			})
		},
	},
}
